import { z } from 'zod'
import type { GridFilterItem, GridFilterModel, GridLogicOperator } from '@mui/x-data-grid-premium'

// GridLogicOperator is a nominal MUI enum whose values are exactly 'and' | 'or'.
// We validate those literals locally rather than importing the enum value, so the
// whole MUI/react/exceljs tree stays out of every backend bundle that consumes
// this module. The schema is re-typed to GridLogicOperator so frontend consumers
// that hand this model to MUI's DataGrid keep type-checking. The cast is type-only;
// the runtime object is a ZodEnum, so OpenAPI generation still emits the enum.
const logicOperatorSchema = z.enum(['and', 'or']) as unknown as z.ZodType<GridLogicOperator>

const FilterItemSchema = z.object({
  id: z.union([z.number(), z.string()]),
  field: z.string(),
  value: z.union([z.string(), z.number(), z.array(z.string())]).optional(),
  operator: z.string(),
})

export type FilterItem = z.infer<typeof FilterItemSchema>

export const FilterModelSchema = z.object({
  items: z.array(FilterItemSchema),
  logicOperator: logicOperatorSchema.optional(),
  quickFilterValues: z.array(z.unknown()).optional(),
  quickFilterLogicOperator: logicOperatorSchema.optional(),
  quickFilterExcludeHiddenColumns: z.boolean().optional(),
})

export type FilterModel = z.infer<typeof FilterModelSchema>

export const validateFilterModel = (filterModel: GridFilterModel | undefined | null) => {
  if (!filterModel || filterModel.items.length === 0) return 'empty' as const
  if (!filterModel.items.every(filterItemIsValid)) return 'incomplete' as const

  return undefined
}

export const filterItemIsValid = (filterItem: GridFilterItem): filterItem is FilterItem => {
  if (filterItem.operator === 'isEmpty' || filterItem.operator === 'isNotEmpty') {
    return true
  }

  if (typeof filterItem.value === 'number') return true

  const trimmedValue = trimIfString(filterItem.value)
  return !!trimmedValue
}

const trimIfString = (value: unknown) => (typeof value === 'string' ? value.trim() : value)
